import React, { useState, useEffect } from 'react'
import './index.css'
import { Layout, Typography, Card, Table, Button, message, Space } from 'antd'
import { DownloadOutlined, LinkOutlined } from '@ant-design/icons'
import ExportJsonExcel from 'js-export-excel'
import axios from 'axios'
import { Link } from 'react-router-dom'

const { Content } = Layout
const { Title } = Typography

// 模块级多页缓存，组件卸载/翻页后已加载过的页面不再请求
const pageCache = {}

const PathogenicAnalysis = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15 })

  const cacheKey = (page, size) => `${page}_${size}`

  const fetchData = async (page, pageSize) => {
    const key = cacheKey(page, pageSize)
    if (pageCache[key]) {
      setData(pageCache[key].data)
      setTotal(pageCache[key].total)
      return
    }
    setLoading(true)
    try {
      const res = await axios.get('/api/pathogenic/samples', { params: { page, pageSize } })
      const processed = res.data.data.map((item, idx) => {
        const pathogenicReads = Number(item.pathogenicReads) || 0
        const totalReads = Number(item.totalReads) || 0
        const ratio = totalReads > 0 ? ((pathogenicReads / totalReads) * 100).toFixed(2) : '0.00'
        return {
          ...item,
          key: idx,
          pathogenicRatio: totalReads,
          pathogenicCount: pathogenicReads,
        }
      })
      pageCache[key] = { data: processed, total: res.data.total }
      setData(processed)
      setTotal(res.data.total)
    } catch (err) {
      message.error('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize)
  }, [])

  const handlePageChange = (page, pageSize) => {
    setPagination({ current: page, pageSize })
    fetchData(page, pageSize)
  }

  const columns = [
    {
      title: 'Run',
      dataIndex: 'run',
      key: 'run',
      width: 140,
      render: (value) => (
        <Link to={`/sample/${value}`} style={{ color: '#1677ff', fontWeight: 600 }}>{value}</Link>
      ),
    },
    {
      title: 'BioProject',
      dataIndex: 'bioProject',
      key: 'bioProject',
      width: 160,
      render: (value) => (
        <a href={`https://www.ncbi.nlm.nih.gov/bioproject/?term=${value}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1677ff' }}>
          <Space><LinkOutlined />{value}</Space>
        </a>
      ),
    },
    {
      title: 'Habitat',
      dataIndex: 'organism',
      key: 'organism',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Pathogenic Bins(Total Bins)',
      dataIndex: 'pathogenicRatio',
      key: 'pathogenicRatio',
      width: 220,
      render: (ratio, record) => (
        <Link
          to={`/pathogenic/${record.run}`}
          style={{ color: '#1677ff', fontWeight: 600 }}
        >
          {record.pathogenicCount} ({ratio})
        </Link>
      ),
    },
  ]

  const fixedCardStyle = {
    width: '100%',
    maxWidth: 1200,
    margin: '0 auto 20px auto', // 上下0，左右auto，底部20
    borderRadius: 12,
    flexShrink: 0,
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '24px 16px', maxWidth: 1400, margin: '0 auto' }}>

        <Card style={fixedCardStyle} bodyStyle={{ padding: '16px 20px' }}>
          <Title level={2} style={{ margin: 0, color: '#002156', fontSize: 22 }}>
            Pathogenic Analysis
          </Title>
        </Card>

        <Card style={fixedCardStyle} bodyStyle={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 14, lineHeight: 1.7 }}>
            <strong>Pathogenic samples overview:</strong>
            <p style={{ marginTop: 8, marginBottom: 0 }}>
              Shown below are all samples with their pathogenic bins.<br></br>
              Click on any value in the <strong>Pathogenic Bins(Total Bins)</strong> column to view detailed pathogenic bins information for that run.
            </p>
          </div>
        </Card>

        <Card style={fixedCardStyle} bodyStyle={{ padding: 0 }}>
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '30', '50', '100'],
              showTotal: (total) => `Total ${total} entries`,
              onChange: handlePageChange,
              onShowSizeChange: (current, size) => {
                setPagination({ current: 1, pageSize: size })
                fetchData(1, size)
              },
            }}
            scroll={{ x: 'max-content' }}
            bordered={false}
          />
        </Card>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button type="primary" icon={<DownloadOutlined />} onClick={async () => {
            message.loading('Fetching all data...', 0)
            try {
              const res = await axios.get('/api/pathogenic/samples', { params: { page: 1, pageSize: 99999 } })
              const allData = res.data.data
              if (!allData.length) { message.destroy(); message.warning('No data to export'); return }
              const exportData = allData.map(d => ({
                Run: d.run || '', BioProject: d.bioProject || '', Habitat: d.organism || '',
                Country: d.country || '', 'Pathogenic Reads': d.pathogenicReads || 0,
                'Total Reads': d.totalReads || 0,
                Ratio: (Number(d.totalReads) > 0 ? ((Number(d.pathogenicReads) / Number(d.totalReads)) * 100).toFixed(2) : '0.00') + '%',
              }))
              const option = { fileName: 'LRMrepo_Pathogenic_all', datas: [{ sheetData: exportData, sheetName: 'Pathogenic', sheetHeader: Object.keys(exportData[0]) }] }
              new ExportJsonExcel(option).saveExcel()
              message.destroy()
              message.success('Export successfully')
            } catch (err) { message.destroy(); message.error('Export failed') }
          }}>Export All to Excel</Button>
        </div>

      </Content>
    </Layout>
  )
}

export default PathogenicAnalysis
